/**
 * SliceSlotType - Enumeration of slot types
 *
 * Defines the types of slots and fingers used in joinery.
 * Slots are cutouts that allow pieces to interlock.
 *
 * Based on cc.sketchchair.geometry.SliceSlot.java
 */

/**
 * Slot type enumeration
 * @readonly
 * @enum {number}
 */
export const SliceSlotType = Object.freeze({
    /**
     * Simple rectangular slot
     * A single rectangular cutout that accepts a mating piece.
     * Used for basic cross-slice intersections.
     */
    SLOT: 1,

    /**
     * Finger protrusion
     * A single protruding tab (the opposite of a slot).
     * Typically used in combination with slots on mating pieces.
     */
    FINGER: 2,

    /**
     * Alternating slots and fingers
     * Creates an interlocking finger joint pattern with alternating
     * slots and fingers along a length.
     *
     * Pattern example:
     * ████▯▯▯▯████▯▯▯▯████  (slots)
     * ▯▯▯▯████▯▯▯▯████▯▯▯▯  (fingers)
     *
     * Properties:
     * - numSlots: Total number of alternating sections
     * - startOnSlot: Whether pattern starts with slot (true) or finger (false)
     * - slotLength: Length of each slot section
     * - fingerLength: Length of each finger section
     *
     * Used for:
     * - Slat-to-slice connections
     * - High-strength joints
     * - Distributing load across multiple contact points
     */
    SLOTS_AND_FINGER: 3
});

/**
 * Get human-readable name for slot type
 * @param {number} type - Slot type value
 * @returns {string} Type name
 */
export function getSlotTypeName(type) {
    const names = {
        [SliceSlotType.SLOT]: 'Slot',
        [SliceSlotType.FINGER]: 'Finger',
        [SliceSlotType.SLOTS_AND_FINGER]: 'Finger Joint'
    };
    return names[type] || 'Unknown';
}

/**
 * Check if slot type creates multiple sections
 * @param {number} type - Slot type value
 * @returns {boolean} True if creates multiple sections
 */
export function isMultiSectionType(type) {
    return type === SliceSlotType.SLOTS_AND_FINGER;
}

/**
 * Check if slot type is additive (finger) or subtractive (slot)
 * @param {number} type - Slot type value
 * @returns {string} 'additive', 'subtractive', or 'both'
 */
export function getSlotOperation(type) {
    if (type === SliceSlotType.FINGER) {
        return 'additive';
    } else if (type === SliceSlotType.SLOT) {
        return 'subtractive';
    } else {
        return 'both';
    }
}
